<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'
import IconSearch from '@/components/icon/IconSearch.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'

/**
 * Searchable single-select for language codes. A button opens a panel with a
 * filter box and the option list; Enter/arrows/Escape work as in a listbox.
 * `options` is [{ code, name, hint?, tag?, disabledReason? }]; `disabledCodes`
 * greys entries out (for codes already chosen elsewhere) while keeping them
 * visible. `tag` is a small label such as "Gemini".
 */
const props = defineProps({
	modelValue: { type: String, default: '' },
	options: { type: Array, default: () => [] },
	disabledCodes: { type: Array, default: () => [] },
	placeholder: { type: String, default: 'Choose a language' },
	searchPlaceholder: { type: String, default: 'Search languages' },
	ariaLabel: { type: String, default: 'Language' },
	disabled: { type: Boolean, default: false },
	// Visual variant of the trigger: 'field' (full-width select look) or 'chip'
	// (compact dashed "add" button used inside the multi-select).
	variant: { type: String, default: 'field' },
	// When set, the trigger renders this instead of the selected value.
	triggerLabel: { type: String, default: '' },
	id: { type: String, default: undefined },
})

const emit = defineEmits(['update:modelValue', 'select'])

const root = ref(null)
const searchEl = ref(null)
const listEl = ref(null)
const open = ref(false)
const query = ref('')
const activeIndex = ref(-1)

// Codes disabled by the parent (already chosen) or by the option itself
// (`disabledReason`, from the English-hub translation rules).
const disabledSet = computed(() => {
	const set = new Set(props.disabledCodes)
	for (const o of props.options) if (o.disabledReason) set.add(o.code)
	return set
})

function disabledLabel(option) {
	if (option.disabledReason) return option.disabledReason
	if (props.disabledCodes.includes(option.code)) return 'added'
	return ''
}

const filtered = computed(() => {
	const q = query.value.trim().toLowerCase()
	if (!q) return props.options
	return props.options.filter((o) =>
		o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q),
	)
})

const selectedOption = computed(() => props.options.find((o) => o.code === props.modelValue) || null)

const triggerText = computed(() => {
	if (props.triggerLabel) return props.triggerLabel
	return selectedOption.value ? selectedOption.value.name : props.placeholder
})

const triggerClass = computed(() => {
	if (props.variant === 'chip') {
		return 'inline-flex h-8 items-center gap-1.5 rounded-full border border-dashed border-surface-5 px-3 text-tiny font-medium text-surface-8 transition-colors hover:border-surface-7 hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
	}
	return [
		'flex h-8 w-full items-center gap-2 rounded bg-surface-3 px-3 text-left text-tiny transition-colors hover:bg-surface-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-main disabled:cursor-not-allowed disabled:text-surface-7',
		selectedOption.value ? 'text-surface-text-2' : 'text-surface-8',
	].join(' ')
})

async function show() {
	if (props.disabled) return
	open.value = true
	query.value = ''
	activeIndex.value = Math.max(0, filtered.value.findIndex((o) => o.code === props.modelValue && !disabledSet.value.has(o.code)))
	await nextTick()
	searchEl.value?.focus()
}

function hide() {
	open.value = false
	activeIndex.value = -1
}

function toggle() {
	if (open.value) hide()
	else show()
}

function choose(option) {
	if (!option || disabledSet.value.has(option.code)) return
	emit('update:modelValue', option.code)
	emit('select', option.code)
	hide()
}

function moveActive(delta) {
	const list = filtered.value
	if (!list.length) return
	let next = activeIndex.value
	for (let i = 0; i < list.length; i += 1) {
		next = (next + delta + list.length) % list.length
		if (!disabledSet.value.has(list[next].code)) break
	}
	activeIndex.value = next
	nextTick(() => {
		const el = listEl.value?.querySelector(`[data-index="${next}"]`)
		el?.scrollIntoView?.({ block: 'nearest' })
	})
}

function onKeydown(event) {
	switch (event.key) {
		case 'ArrowDown':
			event.preventDefault()
			moveActive(1)
			break
		case 'ArrowUp':
			event.preventDefault()
			moveActive(-1)
			break
		case 'Enter':
			event.preventDefault()
			choose(filtered.value[activeIndex.value])
			break
		case 'Escape':
			event.preventDefault()
			hide()
			break
		default:
			break
	}
}

function onDocumentClick(event) {
	if (!open.value) return
	if (root.value?.contains(event.target)) return
	hide()
}

watch(filtered, (list) => {
	if (activeIndex.value >= list.length) activeIndex.value = list.length ? 0 : -1
})

// Escape closes the panel wherever focus ended up (e.g. after clicking a
// disabled option, which is not focusable).
function onDocumentKeydown(event) {
	if (open.value && event.key === 'Escape') hide()
}

onMounted(() => {
	document.addEventListener('mousedown', onDocumentClick)
	document.addEventListener('keydown', onDocumentKeydown)
})
onBeforeUnmount(() => {
	document.removeEventListener('mousedown', onDocumentClick)
	document.removeEventListener('keydown', onDocumentKeydown)
})
</script>

<template>
	<div ref="root" class="relative" :class="variant === 'chip' ? 'inline-block' : 'w-full'">
		<button
			:id="id"
			type="button"
			:class="triggerClass"
			:aria-label="ariaLabel"
			aria-haspopup="listbox"
			:aria-expanded="open"
			:disabled="disabled"
			@click="toggle"
			@keydown.down.prevent="show"
		>
			<slot name="trigger-icon" />
			<span class="min-w-0 flex-1 truncate">{{ triggerText }}</span>
			<icon-base v-if="variant !== 'chip'" class="ml-auto shrink-0 text-surface-7"><icon-chevron-down /></icon-base>
		</button>

		<div
			v-if="open"
			class="absolute left-0 z-30 mt-1 w-full min-w-[240px] overflow-hidden rounded-md bg-surface-2 shadow-dropdown ring-1 ring-white/5"
			:class="variant === 'chip' ? 'w-[260px]' : ''"
			@keydown="onKeydown"
		>
			<div class="relative border-b border-surface-3 p-2">
				<icon-base class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-surface-7"><icon-search /></icon-base>
				<input
					ref="searchEl"
					v-model="query"
					type="text"
					class="h-8 w-full rounded bg-surface-3 !pl-8 pr-2 text-tiny text-surface-text-2 placeholder:text-surface-7 focus:bg-surface-4 focus:outline-none"
					:placeholder="searchPlaceholder"
					aria-label="Search languages"
					autocomplete="off"
					spellcheck="false"
				>
			</div>
			<ul ref="listEl" class="m-0 max-h-56 list-none overflow-y-auto p-1" role="listbox" :aria-label="ariaLabel">
				<li
					v-for="(option, index) in filtered"
					:key="option.code"
					:data-index="index"
					role="option"
					:aria-selected="option.code === modelValue"
					:aria-disabled="disabledSet.has(option.code)"
					class="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-tiny"
					:class="[
						disabledSet.has(option.code) ? 'cursor-not-allowed text-surface-6' : 'text-surface-text-1 hover:bg-surface-3 hover:text-white',
						index === activeIndex && !disabledSet.has(option.code) ? 'bg-surface-3 text-white' : '',
					]"
					:title="disabledLabel(option) || option.hint || ''"
					@mouseenter="activeIndex = index"
					@click="choose(option)"
				>
					<span class="flex min-w-0 flex-1 flex-col">
						<span class="truncate">{{ option.name }}</span>
						<span v-if="option.disabledReason" class="truncate text-[11px] text-surface-6">{{ option.disabledReason }}</span>
						<span v-else-if="option.hint" class="truncate text-[11px] text-surface-7">{{ option.hint }}</span>
					</span>
					<span v-if="option.tag" class="shrink-0 rounded bg-main-transparent px-1 text-[10px] font-semibold text-main-light" data-testid="option-tag">{{ option.tag }}</span>
					<span class="shrink-0 font-mono text-[11px] uppercase text-surface-6">{{ option.code }}</span>
					<icon-base v-if="option.code === modelValue" class="shrink-0 text-main-light" fill="none"><icon-checkmark /></icon-base>
					<span v-else-if="disabledSet.has(option.code) && !option.disabledReason" class="shrink-0 text-[10px] uppercase tracking-wide text-surface-6">added</span>
				</li>
				<li v-if="filtered.length === 0" class="px-2 py-3 text-center text-tiny text-surface-7">No language matches "{{ query }}".</li>
			</ul>
		</div>
	</div>
</template>
